import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import {
  Button,
  ChevronLeftIcon,
  Label,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
} from '../../../components/ui';
import { fetcher } from '../../../services/fetcher';
import { createAccAccount, updateAccAccount } from '../../../services/mutations';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { toAccountPayload } from './config';
import type { AccAccount, AccAccountTreeNode } from './useData';

export type CoaFormMode = 'group' | 'account';

type Lookup = { id: number; code: string; name: string; isActive?: boolean };

type Props = {
  mode: CoaFormMode;
  parentId: number | null;
  editItem: AccAccountTreeNode | AccAccount | null;
  onCancel: () => void;
  onSaved: () => void;
};

type FormState = {
  code: string;
  name: string;
  accountTypeId: string;
  operationalTypeId: string;
  departmentId: string;
  allowOpenItems: boolean;
  paymentTermsDays: string;
  isIntercompany: boolean;
  isBudget: boolean;
  isActive: boolean;
  currencyIds: number[];
  companyIds: number[];
};

const emptyForm = (): FormState => ({
  code: '',
  name: '',
  accountTypeId: '',
  operationalTypeId: '',
  departmentId: '',
  allowOpenItems: false,
  paymentTermsDays: '',
  isIntercompany: false,
  isBudget: false,
  isActive: true,
  currencyIds: [],
  companyIds: [],
});

const FieldCard: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, description, children, className }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border p-4 sm:p-5 space-y-4', colors.border, colors.surface, className)}>
      <div>
        <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
        {description && <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p>}
      </div>
      {children}
    </section>
  );
};

const CheckboxRow: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ label, description, checked, onChange, disabled }) => {
  const { colors } = useTheme();
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-lg px-3 py-2.5 cursor-pointer select-none',
        colors.surfaceHover,
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        aria-label={label}
      />
      <div className="min-w-0">
        <p className={cn('text-sm font-medium', colors.text)}>{label}</p>
        {description && <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p>}
      </div>
    </label>
  );
};

/** Inline right-panel form (no popup). */
export const CoaNodeFormPanel: React.FC<Props> = ({
  mode,
  parentId,
  editItem,
  onCancel,
  onSaved,
}) => {
  const { colors } = useTheme();
  const isEdit = editItem != null;
  const isGroup = mode === 'group';
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const { data: typesRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>('/acc-account-types?page=1&limit=500', fetcher);
  const { data: opTypesRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>(
    '/acc-operational-types?page=1&limit=500',
    fetcher,
  );
  const { data: deptsRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>(
    !isGroup ? '/departments?page=1&limit=500' : null,
    fetcher,
  );
  const { data: currenciesRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>(
    !isGroup ? '/currencies?page=1&limit=500' : null,
    fetcher,
  );
  const { data: companiesRaw } = useSWR<{ data?: Lookup[] } | Lookup[]>(
    !isGroup ? '/acc-companies?page=1&limit=500' : null,
    fetcher,
  );

  const fsTypeOptions = useMemo(() => {
    const list = (Array.isArray(typesRaw) ? typesRaw : typesRaw?.data ?? []).filter((t) => t.isActive !== false);
    return [
      { value: '', label: 'None' },
      ...list
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((t) => ({ value: String(t.id), label: t.name })),
    ];
  }, [typesRaw]);

  const operationalTypeOptions = useMemo(() => {
    const list = (Array.isArray(opTypesRaw) ? opTypesRaw : opTypesRaw?.data ?? []).filter((t) => t.isActive !== false);
    return [
      { value: '', label: 'None' },
      ...list
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((t) => ({ value: String(t.id), label: t.name })),
    ];
  }, [opTypesRaw]);

  const deptOptions = useMemo(() => {
    const list = Array.isArray(deptsRaw) ? deptsRaw : deptsRaw?.data ?? [];
    return [
      { value: '', label: 'None' },
      ...list.filter((t) => t.isActive !== false).map((t) => ({ value: String(t.id), label: t.name })),
    ];
  }, [deptsRaw]);

  const currencyOptions = useMemo(() => {
    const list = Array.isArray(currenciesRaw) ? currenciesRaw : currenciesRaw?.data ?? [];
    return list.filter((t) => t.isActive !== false).map((t) => ({ value: t.id, label: t.name }));
  }, [currenciesRaw]);

  const companyOptions = useMemo(() => {
    const list = Array.isArray(companiesRaw) ? companiesRaw : companiesRaw?.data ?? [];
    return list.filter((t) => t.isActive !== false).map((t) => ({ value: t.id, label: t.name }));
  }, [companiesRaw]);

  useEffect(() => {
    if (editItem) {
      setForm({
        code: editItem.code ?? '',
        name: editItem.name ?? '',
        accountTypeId: editItem.accountTypeId ? String(editItem.accountTypeId) : '',
        operationalTypeId: editItem.operationalTypeId ? String(editItem.operationalTypeId) : '',
        departmentId: editItem.departmentId ? String(editItem.departmentId) : '',
        allowOpenItems: !!editItem.allowOpenItems,
        paymentTermsDays: editItem.paymentTermsDays != null ? String(editItem.paymentTermsDays) : '',
        isIntercompany: !!editItem.isIntercompany,
        isBudget: !!editItem.isBudget,
        isActive: editItem.isActive !== false,
        currencyIds: editItem.currencyIds ?? [],
        companyIds: editItem.companyIds ?? [],
      });
    } else {
      setForm(emptyForm());
    }
  }, [editItem, mode, parentId]);

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Code and name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = toAccountPayload({
        parentId: isEdit ? (editItem?.parentId ?? parentId ?? undefined) : (parentId ?? undefined),
        code: form.code.trim(),
        name: form.name.trim(),
        isPosting: !isGroup,
        accountTypeId: form.accountTypeId || null,
        operationalTypeId: form.operationalTypeId || null,
        departmentId: isGroup ? null : form.departmentId || null,
        allowOpenItems: isGroup ? false : form.allowOpenItems,
        paymentTermsDays: isGroup ? '' : form.paymentTermsDays,
        isIntercompany: isGroup ? false : form.isIntercompany,
        isBudget: isGroup ? false : form.isBudget,
        isActive: form.isActive,
        currencyIds: isGroup ? [] : form.currencyIds,
        companyIds: isGroup ? [] : form.companyIds,
      });

      if (isEdit && editItem) {
        await updateAccAccount(editItem.id, payload);
        toast.success(isGroup ? 'Group updated' : 'Account updated');
      } else {
        await createAccAccount(payload);
        toast.success(isGroup ? 'Group created' : 'Account created');
      }
      onSaved();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit
    ? isGroup
      ? 'Edit group'
      : 'Edit account'
    : isGroup
      ? parentId
        ? 'New subgroup'
        : 'New group'
      : 'New account';

  return (
    <form onSubmit={onSubmit} className="flex flex-col h-full min-h-0">
      <div className={cn('flex-shrink-0 flex items-center gap-2 px-4 py-2.5 border-b', colors.border, colors.surfaceHover)}>
        <button
          type="button"
          onClick={onCancel}
          className={cn('p-1 rounded-md', colors.textMuted, colors.interactiveHover)}
          aria-label="Back"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className={cn('text-sm font-semibold', colors.text)}>{title}</h2>
          <p className={cn('text-xs', colors.textMuted)}>
            {isGroup
              ? 'Header group — no journal posting. Types inherit to child ledgers.'
              : 'Ledger account under the selected group.'}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={saving}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </div>

      <div className={cn('flex-1 overflow-y-auto dashboard-scrollbar p-4 sm:p-5', colors.surfaceHover)}>
        <div className="w-full space-y-4">
          <FieldCard title="Identity" description="Code and display name">
            <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-3">
              <div>
                <Label>Code</Label>
                <TextInput value={form.code} onChange={(e) => patch('code', e.target.value)} required />
              </div>
              <div>
                <Label>Name</Label>
                <TextInput value={form.name} onChange={(e) => patch('name', e.target.value)} required />
              </div>
            </div>
          </FieldCard>

          <FieldCard
            title="Classification"
            description={
              isGroup
                ? 'Operational type inherits to ledgers under this group. FS type is for reporting structure.'
                : 'Operational type drives payment pickers; FS type is for reporting.'
            }
          >
            <div className={cn('grid gap-3', isGroup ? 'grid-cols-2' : 'grid-cols-3')}>
              <div>
                <Label>Operational type</Label>
                <Select
                  value={form.operationalTypeId}
                  onChange={(e) => patch('operationalTypeId', e.target.value)}
                  options={operationalTypeOptions}
                  placeholder="None"
                  helperText="BANK / CASH / AP / AR"
                />
              </div>
              <div>
                <Label>Account type (FS)</Label>
                <Select
                  value={form.accountTypeId}
                  onChange={(e) => patch('accountTypeId', e.target.value)}
                  options={fsTypeOptions}
                  placeholder="None"
                  helperText="Asset, Liability…"
                />
              </div>
              {!isGroup && (
                <div>
                  <Label>Department</Label>
                  <Select value={form.departmentId} onChange={(e) => patch('departmentId', e.target.value)} options={deptOptions} />
                </div>
              )}
            </div>
          </FieldCard>

          {!isGroup && (
            <>
              <FieldCard title="Open items & flags">
                <div className="grid grid-cols-2 gap-3">
                  <CheckboxRow
                    label="Allow open items"
                    description="Track outstanding balances for AP / AR linking"
                    checked={form.allowOpenItems}
                    onChange={(v) => patch('allowOpenItems', v)}
                  />
                  <div className={cn(!form.allowOpenItems && 'opacity-50')}>
                    <Label>Payment terms (days)</Label>
                    <NumberInput
                      value={form.paymentTermsDays}
                      onChange={(e) => patch('paymentTermsDays', String(e.target.value ?? ''))}
                      disabled={!form.allowOpenItems}
                    />
                  </div>
                  <CheckboxRow label="Intercompany" checked={form.isIntercompany} onChange={(v) => patch('isIntercompany', v)} />
                  <CheckboxRow label="Budget account" checked={form.isBudget} onChange={(v) => patch('isBudget', v)} />
                </div>
              </FieldCard>

              <FieldCard title="Restrictions" description="Leave empty to allow all">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Allowed currencies</Label>
                    <MultiSelect
                      options={currencyOptions}
                      value={form.currencyIds}
                      onChange={(v) => patch('currencyIds', v.map(Number).filter((n) => Number.isFinite(n)))}
                      placeholder="All currencies"
                    />
                  </div>
                  <div>
                    <Label>Allowed companies</Label>
                    <MultiSelect
                      options={companyOptions}
                      value={form.companyIds}
                      onChange={(v) => patch('companyIds', v.map(Number).filter((n) => Number.isFinite(n)))}
                      placeholder="All companies"
                    />
                  </div>
                </div>
              </FieldCard>
            </>
          )}

          <FieldCard title="Status">
            <CheckboxRow
              label="Active"
              description={isGroup ? 'Inactive groups stay in the tree but are marked inactive' : 'Inactive accounts are hidden from pickers'}
              checked={form.isActive}
              onChange={(v) => patch('isActive', v)}
            />
          </FieldCard>
        </div>
      </div>
    </form>
  );
};
